import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { istNow, ymd } from "../lib/dates";

const CATS = ["Rent", "Maintenance", "Electricity", "Staff", "Products", "Marketing", "General", "Other"];
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "Card", "Other"];
const today = () => ymd(istNow());
const blank = (bid) => ({ branch_id: bid || "", date: today(), category: "Rent", amount: 0, payment_mode: "Cash", paid_to: "", reference_no: "", description: "", staff_id: "", is_advance: false });

export default function Expenses() {
  const { branches, branchParam } = useBranch();
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const isAdmin = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const canAdd = isAdmin || !!perms.add_expense;
  const canEdit = isAdmin || !!perms.edit_expense;
  const canDelete = isAdmin || !!perms.delete_expense;
  const isReception = user?.role === "RECEPTION";
  const cats = isReception ? ["General", "Other"] : CATS;

  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/expenses", { params: { branch_id: branchParam } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [branchParam]);

  useEffect(() => {
    if (form.category === "Staff" && form.branch_id) {
      api.get("/staff", { params: { branch_id: form.branch_id } }).then((r) => setStaff(r.data)).catch(() => setStaff([]));
    }
  }, [form.category, form.branch_id]);

  const openAdd = () => { setEditId(null); setForm({ ...blank(branches[0]?.id || ""), category: cats[0] }); setOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ branch_id: r.branch_id, date: r.date, category: r.category, amount: r.amount, payment_mode: r.payment_mode || "Cash",
      paid_to: r.paid_to || "", reference_no: r.reference_no || "", description: r.description || "", staff_id: r.staff_id || "", is_advance: !!r.is_advance });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (payload.category !== "Staff") { payload.staff_id = ""; payload.is_advance = false; }
      if (payload.is_advance && !payload.staff_id) { toast.error("Select a staff member for the advance"); setBusy(false); return; }
      if (editId) { await api.put(`/expenses/${editId}`, payload); toast.success("Expense updated"); }
      else { await api.post("/expenses", payload); toast.success(form.is_advance ? "Staff advance recorded (auto-links to payroll)" : "Expense added"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete this ${r.category} expense of ${rupee(r.amount)}?`)) return;
    try { await api.delete(`/expenses/${r.id}`); toast.success("Expense deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const total = rows.reduce((s, r) => s + r.amount, 0);

  return (
    <div>
      <PageHeader title="Expenses" subtitle={`Total: ${rupee(total)} · ${rows.length} record(s)`}
        actions={canAdd && <Button onClick={openAdd} data-testid="add-expense-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Expense</Button>} />

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Date</TableHead><TableHead>Branch</TableHead><TableHead>Category</TableHead>
              <TableHead>Paid To</TableHead><TableHead>Mode</TableHead><TableHead>Added By</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              {(canEdit || canDelete) && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="border-border" data-testid={`expense-row-${r.id}`}>
                <TableCell>{r.date}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{(branches.find((b) => b.id === r.branch_id) || {}).name || "—"}</TableCell>
                <TableCell><Badge variant="outline">{r.category}</Badge>{r.is_advance && <Badge variant="outline" className="ml-1 border-primary text-primary text-[10px]">Advance</Badge>}</TableCell>
                <TableCell className="text-muted-foreground">{r.paid_to || "—"}<span className="block text-[11px]">{r.description}</span></TableCell>
                <TableCell>{r.payment_mode}{r.reference_no && <span className="block text-[11px] text-muted-foreground">#{r.reference_no}</span>}</TableCell>
                <TableCell className="text-xs">{r.created_by}<span className="block text-[11px] text-muted-foreground">{r.created_by_role}{r.created_at ? ` · ${new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}` : ""}</span></TableCell>
                <TableCell className="text-right text-primary">{rupee(r.amount)}</TableCell>
                {(canEdit || canDelete) && (
                  <TableCell className="text-right whitespace-nowrap">
                    {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`expense-edit-${r.id}`}><Pencil size={14} /></Button>}
                    {canDelete && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(r)} data-testid={`expense-delete-${r.id}`}><Trash2 size={14} /></Button>}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No expenses.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Expense" : "Add Expense"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Branch</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="expense-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, ...(v !== "Staff" ? { staff_id: "", is_advance: false } : {}) })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.category === "Staff" && !editId && (
              <div className="rounded-sm border border-primary/30 bg-primary/5 p-3 space-y-3">
                <div><Label className="text-xs">Staff Name {form.is_advance && <span className="text-destructive">*</span>}</Label>
                  <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
                    <SelectTrigger className="bg-secondary mt-1" data-testid="expense-staff"><SelectValue placeholder="Select staff" /></SelectTrigger>
                    <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="is-advance" checked={form.is_advance} data-testid="expense-is-advance"
                    onCheckedChange={(v) => setForm({ ...form, is_advance: !!v, description: !!v && !form.description ? "Advance" : form.description })} />
                  <Label htmlFor="is-advance" className="text-xs cursor-pointer">Mark as Salary Advance (auto-deducts from this staff's payroll)</Label>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-secondary mt-1" data-testid="expense-date" /></div>
              <div><Label className="text-xs">Amount (₹)</Label><Input type="number" data-testid="expense-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="expense-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Reference No.</Label><Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="bg-secondary mt-1" data-testid="expense-ref" placeholder="Txn / cheque no." /></div>
            </div>
            <div><Label className="text-xs">Paid To</Label><Input value={form.paid_to} onChange={(e) => setForm({ ...form, paid_to: e.target.value })} className="bg-secondary mt-1" data-testid="expense-paidto" placeholder="Vendor / person" /></div>
            <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-secondary mt-1" data-testid="expense-desc" /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="expense-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
